import type {
	DiscriminatorFeature,
	DiscriminatorSpecFields,
} from "@sitescene/types";

import { useState } from "react";

import { Button } from "@/components/ui/buttons/button";
import { Checkbox } from "@/components/ui/checkbox/checkbox";
import { Input } from "@/components/ui/input/input";
import { Label } from "@/components/ui/input/label";
import { NativeSelect } from "@/components/ui/select/select-native";
import { TextArea } from "@/components/ui/textarea/textarea";

import { ListInput } from "./ListInput";
import { FEATURE_KEY_PATTERN } from "./taxonomy-view";

interface SpecEditorProps {
	busy: boolean;
	/** Class ids to offer as confusers, excluding the class itself. */
	classOptions: { label: string; value: string }[];
	error: Error | null;
	/**
	 * What the form starts from when that differs from `value`, the baseline it
	 * is compared against — suggested features to review, say.
	 */
	initial?: DiscriminatorSpecFields;
	/**
	 * Keys the live spec already has. Those are locked: a key is what past
	 * verifications and calibration refer to, so renaming one is remove + add.
	 */
	lockedKeys: string[];
	onRevert?: () => void;
	onSave: (fields: DiscriminatorSpecFields) => void;
	/** Label for the id prefix of every control. */
	prefix: string;
	staged: boolean;
	value: DiscriminatorSpecFields;
}

const EMPTY_FEATURE: DiscriminatorFeature = {
	description: "",
	key: "",
	measurable: false,
};

/**
 * Confusers and features for one spec. Holds its edits locally until saved to
 * the draft; the parent remounts it (by `key`) when the value it should start
 * from changes.
 */
export const SpecEditor = ({
	busy,
	classOptions,
	error,
	initial,
	lockedKeys,
	onRevert,
	onSave,
	prefix,
	staged,
	value,
}: SpecEditorProps) => {
	const [fields, setFields] = useState(initial ?? value);
	const locked = new Set(lockedKeys);
	const setFeature = (index: number, changes: Partial<DiscriminatorFeature>) =>
		setFields((current) => ({
			...current,
			features: current.features.map((feature, i) =>
				i === index ? { ...feature, ...changes } : feature,
			),
		}));
	const move = (index: number, offset: number) =>
		setFields((current) => {
			const features = [...current.features];
			const [moved] = features.splice(index, 1);
			features.splice(index + offset, 0, moved!);
			return { ...current, features };
		});

	const keys = fields.features.map((feature) => feature.key.trim());
	const badKey = keys.find(
		(key, i) => !FEATURE_KEY_PATTERN.test(key) || keys.indexOf(key) !== i,
	);
	const dirty = JSON.stringify(fields) !== JSON.stringify(value);

	return (
		<div className="flex flex-col gap-3">
			<Checkbox
				isSelected={fields.active}
				label="Spec active"
				onChange={(active) => setFields({ ...fields, active })}
			/>
			<div className="flex flex-col gap-1.5">
				<Label htmlFor={`${prefix}-spec-confusers`}>Look-alikes</Label>
				<ListInput
					id={`${prefix}-spec-confusers`}
					onChange={(confusers) => setFields({ ...fields, confusers })}
					options={classOptions}
					values={fields.confusers}
				/>
			</div>

			<ol className="flex flex-col gap-3">
				{fields.features.map((feature, index) => {
					const keyLocked = locked.has(feature.key);
					return (
						<li
							className="flex flex-col gap-2 rounded border border-secondary p-3"
							key={index}
						>
							<div className="flex flex-wrap items-end gap-3">
								<Input
									isDisabled={keyLocked}
									label="Key"
									onChange={(key) => setFeature(index, { key })}
									placeholder="rigid-straight-object"
									value={feature.key}
								/>
								<Input
									label="Group (correlated cues)"
									onChange={(group) => setFeature(index, { group })}
									value={feature.group ?? ""}
								/>
								<NativeSelect
									label="Seeing it points to"
									onChange={(event) =>
										setFeature(index, {
											polarity: event.target
												.value as DiscriminatorFeature["polarity"],
										})
									}
									options={[
										{ label: "the class", value: "candidate" },
										{ label: "a look-alike", value: "confuser" },
									]}
									value={feature.polarity ?? "candidate"}
								/>
								<Checkbox
									className="pb-1"
									isSelected={feature.measurable}
									label="Measurable"
									onChange={(measurable) => setFeature(index, { measurable })}
								/>
								<Checkbox
									className="pb-1"
									isSelected={feature.requiredToAlert ?? false}
									label="Required to alert"
									onChange={(requiredToAlert) =>
										setFeature(index, { requiredToAlert })
									}
								/>
								<Checkbox
									className="pb-1"
									isSelected={feature.ignoreAbsent ?? false}
									label="Absence says nothing"
									onChange={(ignoreAbsent) =>
										setFeature(index, { ignoreAbsent })
									}
								/>
								<Checkbox
									className="pb-1"
									isSelected={feature.heard ?? false}
									label="Judged by ear"
									onChange={(heard) => setFeature(index, { heard })}
								/>
								<div className="ml-auto flex gap-1">
									<Button
										color="secondary"
										isDisabled={index === 0}
										onClick={() => move(index, -1)}
										size="sm"
										type="button"
									>
										↑
									</Button>
									<Button
										color="secondary"
										isDisabled={index === fields.features.length - 1}
										onClick={() => move(index, 1)}
										size="sm"
										type="button"
									>
										↓
									</Button>
									<Button
										color="primary-destructive"
										onClick={() =>
											setFields({
												...fields,
												features: fields.features.filter((_, i) => i !== index),
											})
										}
										size="sm"
										type="button"
									>
										Remove
									</Button>
								</div>
							</div>
							<TextArea
								label="What to look at"
								onChange={(description) => setFeature(index, { description })}
								rows={2}
								value={feature.description}
							/>
						</li>
					);
				})}
			</ol>

			<div className="flex flex-wrap items-center gap-2">
				<Button
					color="secondary"
					onClick={() =>
						setFields({
							...fields,
							features: [...fields.features, EMPTY_FEATURE],
						})
					}
					size="sm"
					type="button"
				>
					Add feature
				</Button>
				<Button
					isDisabled={busy || !dirty || Boolean(badKey !== undefined)}
					onClick={() => onSave(fields)}
					size="sm"
					type="button"
				>
					Save features to draft
				</Button>
				{staged && onRevert && (
					<Button
						color="primary-destructive"
						isDisabled={busy}
						onClick={onRevert}
						size="sm"
						type="button"
					>
						Remove from draft
					</Button>
				)}
				{badKey !== undefined && (
					<span className="text-sm text-error-primary" role="alert">
						Feature keys must be unique lowercase kebab-case
						{badKey ? ` ("${badKey}")` : ""}.
					</span>
				)}
				{error && (
					<span className="text-sm text-error-primary" role="alert">
						{error.message}
					</span>
				)}
			</div>
		</div>
	);
};
